import { Component, inject, signal, SimpleChanges } from '@angular/core';
import {
	FormArray,
	FormBuilder,
	FormControl,
	FormGroup,
	ReactiveFormsModule,
} from '@angular/forms';
import { BaseSectionComponent } from '../../../base-classes/sections/base-section.component';
import { VEHICLES_MODULE_SERVICE } from '../../../tokens/vehicles-module-service.token';
import { mapFeatureItemDtoToFeatureItemModel } from '../../../mapper/feature-items.mapper';
import { FeatureItemModel } from '../../../models/feature-item.model';
import { finalize, takeUntil } from 'rxjs';
import { LoadingSpinnerComponent } from '../../loading-spinner/loading-spinner.component';
import { ultraAnimations } from '../../../animations';
import { ToggleNewComponent } from '../../toggle-new/toggle-new.component';
import { TranslocoModule } from '@ngneat/transloco';
interface ControlMap {
	featureItem: FeatureItemModel;
	control: FormControl<boolean>;
}

interface ExtraItems {
	id: number;
	featureItems: FeatureItemModel[];
}

@Component({
	selector: 'app-extra-features-section',
	templateUrl: 'extra-features-section.component.html',
	imports: [
		LoadingSpinnerComponent,
		ToggleNewComponent,
		ReactiveFormsModule,
		TranslocoModule,
	],
	animations: ultraAnimations,
})
export class ExtraFeaturesSectionComponent extends BaseSectionComponent<ExtraItems> {
	protected _skipCount: number = 0;
	isLoading = signal(true);
	#service = inject(VEHICLES_MODULE_SERVICE);
	detailsForm: FormGroup<any> = new FormGroup({});
	form = new FormArray<FormControl<boolean>>([]);
	controlsMap = signal<ControlMap[]>([]);

	constructor(private _formBuilder: FormBuilder) {
		super();
		this.form.valueChanges.subscribe((values) => {
			this.sectionChanged.emit({
				featureItems: this.#constructArrayOfFeatureItems(
					values,
					this.controlsMap(),
				),
			});
		});

		this.#service
			.getExtraFeatureItems()
			.pipe(
				finalize(() => {
					this.isLoading.set(false);
				}),
			)
			.subscribe((res) => {
				this.controlsMap.set(
					mapFeatureItemDtoToFeatureItemModel(res.data).featureItems.map(
						(item) => {
							return {
								featureItem: item,
								control: new FormControl(
									this.#isFeatureItemIncluded(
										item,
										this.recordData().featureItems,
									),
								),
							};
						},
					),
				);
				this.form.clear();
				this.controlsMap().forEach((c) =>
					this.form.push(c.control, { emitEvent: false }),
				);
				this.#reactToEditMode();
			});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.recordEventsService.onCancel
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe(() => {
				this.controlsMap().forEach((item, i) => {
					if (
						this.recordData()
							.featureItems.map((item) => item.id)
							.includes(item.featureItem.id)
					) {
						this.form.at(i)?.setValue(true, { emitEvent: false });
					} else {
						this.form.at(i)?.setValue(false, { emitEvent: false });
					}
				});
			});
	}

	// overwrite base ngOnChanges
	public ngOnChanges(changes: SimpleChanges) {
		if (changes.isEditMode) {
			if (this.form) {
				this.#reactToEditMode();
			}
		}
	}

	#reactToEditMode() {
		if (this.isEditMode) {
			this.form.enable({ emitEvent: false });
		} else {
			this.form.disable({ emitEvent: false });
		}
	}

	#isFeatureItemIncluded(
		item: FeatureItemModel,
		recordDataFeatureItems: FeatureItemModel[],
	) {
		if (!recordDataFeatureItems) {
			return false;
		}
		return recordDataFeatureItems.map((item) => item.id).includes(item.id);
	}

	#constructArrayOfFeatureItems(
		values: boolean[],
		controlsMap: ControlMap[],
	): FeatureItemModel[] {
		return values
			.map((val, i) => (val ? controlsMap[i].featureItem : null))
			.filter((id) => id !== null);
	}

	#getBooleansFromFeatureItems(
		selectedItems: FeatureItemModel[],
		controlsMap: ControlMap[],
	): boolean[] {
		return controlsMap.map((cm) => selectedItems.includes(cm.featureItem));
	}

	_mapModelToForm(): void {} // unused, here just to satisfy the extension

	_mapFormToModel(): Partial<ExtraItems> {
		return {}; // unused, here just to satisfy the extension
	}
}
